/**
 * BlockerDescriptor registry — plain-English copy keyed on stable machine code.
 * P1-A: pure data only; not wired into UI.
 * P2-A: additive `fixScope` (WORKER | TIMESHEET | TENANT) for remedy routing.
 */

import { LEAVE_SETUP_DESCRIPTORS } from '../employee-leave-setup';
import { PAY_CONTEXT_BLOCKER_LABELS, PAY_ROLE_REQUIRED_MESSAGE } from '../payroll-period-readiness';
import {
  allEmittedReadinessCodes,
  isApprovedLeaveReadinessCode,
  isProviderResponsibilityEvidenceCode,
  KNOWN_EXPORT_BLOCKER_CODES,
  KNOWN_PAY_CONTEXT_NESTED_CODES,
  PAY_CONTEXT_RECALCULATION_CODES,
  PAYROLL_RUN_READINESS_ISSUE_CODES as C,
  PERIOD_AGGREGATOR_BLOCKER_CODES,
  providerResponsibilityEvidenceCode,
  providerResponsibilityKindFromCode,
} from './payroll-readiness-issue';

/** Where the primary fix lives — drives P2 scope-chip copy (not cascade claims). */
export type BlockerFixScope = 'WORKER' | 'TIMESHEET' | 'TENANT';

/**
 * What an operational remedy surface must actually be able to do.
 *
 * This is intentionally separate from a route or button label: a non-empty
 * destination is not a remedy when that destination cannot perform the action.
 */
export type BlockerRemedyCapability =
  | 'NAVIGATE'
  | 'REVIEW'
  | 'RESOLVE_ASSIGNMENT_OFFER'
  | 'CORRECT_ACTUAL_TIME'
  | 'CAPTURE_TIMESHEET_PAY_FACTS'
  | 'RECOMPUTE_TIMESHEET'
  | 'APPROVE_TIMESHEET';

export interface BlockerDescriptor {
  /** Stable code key (matches PayrollReadinessIssue.code). */
  code: string;
  /** Plain English title, provider vocabulary. */
  title: string;
  /** One-line why / what to do, no engine jargon. */
  detail: string;
  /**
   * Scope of the primary fix surface (P2-A).
   * Total-lookup: unmapped codes default to TIMESHEET.
   */
  fixScope: BlockerFixScope;
  /** Capability the selected remedy surface must provide. */
  remedyCapability: BlockerRemedyCapability;
}

type DescriptorSeed = Omit<BlockerDescriptor, 'code' | 'fixScope' | 'remedyCapability'>;

/**
 * Pure fixScope map keyed on stable machine code (v3 routing table).
 * Unlisted codes → TIMESHEET via {@link fixScopeForCode}.
 */
const FIX_SCOPE_BY_CODE: Readonly<Record<string, BlockerFixScope>> = Object.freeze({
  PAY_ROLE_CARE_MINUTES_CATEGORY_MISSING: 'TENANT',
  // WORKER — classification / profile / pay-role assignment / salary-flat / award review
  [C.AWARD_CLASSIFICATION_MISSING]: 'WORKER',
  STAFF_CLASSIFICATION_NOT_FOUND: 'WORKER',
  WORKER_MISSING_PAY_PROFILE: 'WORKER',
  WORKED_TIME_HOLD_UNDECIDED: 'WORKER',
  WORKED_TIME_PAYABLE_MISSING: 'TIMESHEET',
  PAY_PROFILE_MISSING_PAY_RULE_VERSION: 'WORKER',
  PAY_RATE_UNSUPPORTED_BY_AGREEMENT: 'WORKER',
  CLASSIFICATION_DIFFERS_FROM_RECORD: 'WORKER',
  XERO_TEMPLATE_DISAGREES: 'WORKER',
  UNPAID_PENDING_LEAVE: 'TIMESHEET',
  STAFF_PAY_PROFILE_NOT_FOUND: 'WORKER',
  STAFF_PAY_PROFILE_AMBIGUOUS: 'WORKER',
  FT_OVER_8H_WITHOUT_10H_AGREEMENT: 'WORKER',
  SALARY_FLAT_CONFIG_INCOMPLETE: 'WORKER',
  SALARY_FLAT_REVIEW_REQUIRED: 'WORKER',
  CONTRACT_HOURLY_CONFIG_MISSING: 'WORKER',
  CONTRACT_HOURLY_RATE_REQUIRED: 'WORKER',
  CONTRACT_WEEKLY_HOURS_REQUIRED: 'WORKER',
  CONTRACT_OVERTIME_TERMS_REQUIRED: 'WORKER',
  CONTRACT_PENALTY_TERMS_REQUIRED: 'WORKER',
  CONTRACT_TERMS_EVIDENCE_REQUIRED: 'WORKER',
  CONTRACT_CONFIGURATION_NOT_APPROVED: 'WORKER',
  PAY_ROLE_ASSIGNMENT_REQUIRED: 'WORKER',
  PAY_ROLE_ASSIGNMENT_STALE: 'WORKER',
  PAY_ROLE_NOT_ACTIVE: 'WORKER',
  PAY_ROLE_ELIGIBILITY_BLOCKED: 'WORKER',
  PAY_ROLE_AMBIGUOUS: 'WORKER',
  PAY_ROLE_MISSING: 'WORKER',
  PAY_ROLE_NOT_FOUND: 'WORKER',
  [C.AWARD_REVIEW_REQUIRED]: 'WORKER',

  // TIMESHEET — event-level / recompute / evidence / facts capture / breaks / approve
  PAY_ROLE_REQUIRED: 'TIMESHEET',
  PAY_ROLE_RESELECTION_REQUIRED: 'TIMESHEET',
  ASSIGNMENT_WORK_SEGMENT_CHANGED: 'TIMESHEET',
  SOURCE_PAY_INPUT_CHANGED: 'TIMESHEET',
  [C.MISSING_COMPLETED_TIME_SOURCE_EVIDENCE]: 'TIMESHEET',
  [C.AUTO_CLOSED_PENDING_CONFIRMATION]: 'TIMESHEET',
  [C.IMPLAUSIBLE_WORKED_DURATION]: 'TIMESHEET',
  [C.POSSIBLE_DUPLICATE_TIMESHEET]: 'TIMESHEET',
  [C.CLOCK_HONESTY]: 'TIMESHEET',
  [C.SERVICE_AGREEMENT_GAP]: 'TIMESHEET',
  [C.CARE_PLAN_GAP]: 'TIMESHEET',
  PAY_FACTS_MISSING: 'TIMESHEET',
  PAY_FACT_SET_REQUIRED: 'TIMESHEET',
  [C.PAY_FACT_NESTED]: 'TIMESHEET',
  [C.PAY_EVENT_FACTS_NOT_READY]: 'TIMESHEET',
  [C.BREAK_DURATION_UNRESOLVED]: 'TIMESHEET',
  [C.BREAK_TREATMENT_BLOCKED]: 'TIMESHEET',
  [C.BREAK_TREATMENT_NOT_RESOLVED]: 'TIMESHEET',
  [C.BREAK_NESTED]: 'TIMESHEET',
  [C.BREAK_TREATMENT_FACTS_MISSING]: 'TIMESHEET',
  [C.BREAK_STATUS_UNKNOWN]: 'TIMESHEET',
  [C.ZERO_PAY]: 'TIMESHEET',
  TIMESHEET_ZERO_PAY: 'TIMESHEET',
  TIMESHEET_PAY_CONTEXT_STALE: 'TIMESHEET',
  TIMESHEET_PAY_CONTEXT_NOT_RESOLVED: 'TIMESHEET',
  [C.PAY_CONTEXT_NOT_RESOLVED]: 'TIMESHEET',
  [C.PAY_CONTEXT_NESTED]: 'TIMESHEET',
  ASSIGNMENT_PAY_CONTEXT_BLOCKED: 'TIMESHEET',
  ASSIGNMENT_PAY_CONTEXT_NOT_RESOLVED: 'TIMESHEET',
  PAY_CONTEXT_ORDINARY_PROFILE_DRIFT: 'TIMESHEET',
  PAY_CONTEXT_RATE_IDENTITY_DRIFT: 'TIMESHEET',
  PAY_CONTEXT_EBA_IDENTITY_DRIFT: 'TIMESHEET',
  PAY_CONTEXT_RULE_IDENTITY_DRIFT: 'TIMESHEET',
  ASSIGNMENT_NO_TIMESHEET: 'TIMESHEET',
  ASSIGNMENT_COVERED_BY_APPROVED_LEAVE: 'TIMESHEET',
  [C.NOT_APPROVED]: 'TIMESHEET',
  TIMESHEET_NOT_APPROVED: 'TIMESHEET',
  TIMESHEET_FACT_SET_CHANGED_PAY_REAPPROVAL_REQUIRED: 'TIMESHEET',
  [C.OPEN_PAYROLL_DISPUTE]: 'TIMESHEET',
  [C.UNSUPPORTED_AWARD]: 'TIMESHEET',
  STAFF_REQUIRED: 'TIMESHEET',
  TIMESHEET_CORRECTION_PENDING_REVIEW: 'TIMESHEET',
  IMPORTED_LEAVE_PENDING_APPROVAL: 'TIMESHEET',
  ASSIGNMENT_OFFER_NOT_RESOLVED: 'TIMESHEET',
  TIMESHEET_REJECTED: 'TIMESHEET',
  TIMESHEET_AND_APPROVED_LEAVE_SAME_DAY: 'TIMESHEET',
  TIMESHEET_ROSTER_ASSIGNMENT_SCOPE_MISMATCH: 'TIMESHEET',
  TIMESHEET_ROSTER_IDENTITY_MISMATCH: 'TIMESHEET',
  TIMESHEET_ROSTER_LIFECYCLE_REVIEW_REQUIRED: 'TIMESHEET',
  TIMESHEET_ROSTER_PERIOD_LINKAGE_REQUIRED: 'TIMESHEET',
  TIMESHEET_ROSTER_PERIOD_STATUS_REVIEW_REQUIRED: 'TIMESHEET',

  // TENANT — rates / EA / export / provider / fact approval (no grid approve)
  RATE_NOT_FOUND: 'TENANT',
  RATE_SCHEDULE_GAP: 'TENANT',
  RATE_CONTEXT_NOT_FOUND: 'TENANT',
  PAY_RULE_TWO_ORACLE_CERTIFICATION_REQUIRED: 'TENANT',
  EBA_EXPORT_NOT_READY: 'TENANT',
  XERO_MAPPING_MISSING: 'TENANT',
  XERO_MAPPING_DRIFT: 'TENANT',
  XERO_COMPONENT_LINES_MISSING: 'TENANT',
  DRY_RUN_NOT_RUN: 'TENANT',
  DRY_RUN_FAILED: 'TENANT',
  DRY_RUN_RECONCILIATION: 'TENANT',
  PAY_FACT_SET_NOT_APPROVED: 'TENANT',
  TIMESHEET_FACT_SET_APPROVAL_PENDING: 'TENANT',
  CONTRACT_HOURLY_MATERIALISER_NOT_CONNECTED: 'TENANT',
  CONTRACT_HOURLY_TREATMENT_NOT_APPROVED: 'WORKER',
  CONTRACT_HOURLY_TREATMENT_INVALID: 'WORKER',
  [providerResponsibilityEvidenceCode('TAX')]: 'TENANT',
});

/** Total-lookup fixScope — unmapped / open-ended families default via rules below. */
export function fixScopeForCode(code: string): BlockerFixScope {
  if (code in LEAVE_SETUP_DESCRIPTORS) return 'WORKER';
  const exact = FIX_SCOPE_BY_CODE[code];
  if (exact) return exact;
  if (isProviderResponsibilityEvidenceCode(code)) return 'TENANT';
  if (isApprovedLeaveReadinessCode(code)) return 'TIMESHEET';
  if (code.startsWith('XERO_MAPPING_') || code.startsWith('DRY_RUN_')) return 'TENANT';
  if (code.includes('_IDENTITY_DRIFT')) return 'TIMESHEET';
  if (code.startsWith('PAY_ROLE_')) return 'WORKER';
  return 'TIMESHEET';
}

/** Total lookup used by the route-capability regression contract. */
export function remedyCapabilityForCode(code: string): BlockerRemedyCapability {
  if (code === 'ASSIGNMENT_OFFER_NOT_RESOLVED') {
    return 'RESOLVE_ASSIGNMENT_OFFER';
  }
  if (
    code === C.MISSING_COMPLETED_TIME_SOURCE_EVIDENCE ||
    code === C.AUTO_CLOSED_PENDING_CONFIRMATION ||
    code === C.IMPLAUSIBLE_WORKED_DURATION ||
    code === C.POSSIBLE_DUPLICATE_TIMESHEET ||
    code === C.CLOCK_HONESTY
  ) {
    return 'CORRECT_ACTUAL_TIME';
  }
  if (
    code === 'PAY_FACTS_MISSING' ||
    code === 'PAY_FACT_SET_REQUIRED' ||
    code === C.PAY_FACT_NESTED ||
    code === C.PAY_EVENT_FACTS_NOT_READY ||
    code === C.BREAK_TREATMENT_BLOCKED ||
    code === C.BREAK_TREATMENT_NOT_RESOLVED ||
    code === C.BREAK_NESTED ||
    code === C.BREAK_TREATMENT_FACTS_MISSING
  ) {
    return 'CAPTURE_TIMESHEET_PAY_FACTS';
  }
  if (
    code === C.ZERO_PAY ||
    code === 'TIMESHEET_ZERO_PAY' ||
    code === 'TIMESHEET_PAY_CONTEXT_STALE' ||
    code === 'TIMESHEET_PAY_CONTEXT_NOT_RESOLVED' ||
    code === C.PAY_CONTEXT_NOT_RESOLVED ||
    code === C.PAY_CONTEXT_NESTED ||
    PAY_CONTEXT_RECALCULATION_CODES.includes(code)
  ) {
    return 'RECOMPUTE_TIMESHEET';
  }
  if (code === C.NOT_APPROVED || code === 'TIMESHEET_NOT_APPROVED') {
    return 'APPROVE_TIMESHEET';
  }
  if (
    code === 'PAY_FACT_SET_NOT_APPROVED' ||
    code === 'TIMESHEET_FACT_SET_APPROVAL_PENDING' ||
    code === 'PAY_ROLE_REQUIRED' ||
    code === 'PAY_ROLE_RESELECTION_REQUIRED' ||
    code === 'STAFF_REQUIRED' ||
    code === 'RATE_CONTEXT_NOT_FOUND' ||
    code === C.AWARD_REVIEW_REQUIRED ||
    code === C.UNSUPPORTED_AWARD ||
    code === C.BREAK_STATUS_UNKNOWN ||
    code === 'CONTRACT_CONFIGURATION_NOT_APPROVED' ||
    code === 'CONTRACT_HOURLY_MATERIALISER_NOT_CONNECTED' ||
    code === 'TIMESHEET_REJECTED' ||
    code === 'TIMESHEET_ROSTER_ASSIGNMENT_SCOPE_MISMATCH' ||
    code === 'TIMESHEET_ROSTER_IDENTITY_MISMATCH' ||
    code === 'TIMESHEET_ROSTER_LIFECYCLE_REVIEW_REQUIRED' ||
    code === 'TIMESHEET_ROSTER_PERIOD_STATUS_REVIEW_REQUIRED' ||
    code === 'XERO_COMPONENT_LINES_MISSING' ||
    code.includes('_IDENTITY_DRIFT')
  ) {
    return 'REVIEW';
  }
  return 'NAVIGATE';
}

const DESCRIPTOR_OVERRIDES: Record<string, DescriptorSeed> = {
  ...LEAVE_SETUP_DESCRIPTORS,
  ...Object.fromEntries(
    PAY_CONTEXT_RECALCULATION_CODES.map((code) => [
      code,
      {
        title: 'Pay needs recalculating',
        detail:
          'Pay inputs changed. Payroll must recheck pricing before payment; verified worked time can still be approved.',
      },
    ]),
  ),
  [C.NOT_APPROVED]: {
    title: 'Timesheet not yet approved',
    detail: 'Approve this timesheet before it can be included in a payroll export.',
  },
  TIMESHEET_NOT_APPROVED: {
    title: 'Timesheet not yet approved',
    detail: 'Manager approval is still pending for this timesheet.',
  },
  TIMESHEET_FACT_SET_APPROVAL_PENDING: {
    title: 'Allowance answers on this approved timesheet still need a second approver',
    detail:
      'The hours are approved, but the allowance answers have not been checked by a second person yet. Approve them before adding this shift to payroll.',
  },
  TIMESHEET_FACT_SET_CHANGED_PAY_REAPPROVAL_REQUIRED: {
    title: 'Pay details changed after approval',
    detail: 'Reopen the approval and approve again so the new pay details are signed off.',
  },
  WORKER_PAY_IDENTITY_CHANGED: {
    title: "This worker's pay setup changed",
    detail:
      'Payroll must review the changed pay setup before payment. Verified worked time can still be approved.',
  },
  // `invalidate_timesheet_pay_context_on_source_change` / the assignment
  // equivalent trigger resets pay_context_status to NOT_RESOLVED with this
  // code whenever worked time, roster assignment, pay role, or source
  // evidence changes in an UPDATE that is not itself a pay-context write —
  // including a recompute's own persist step immediately after the source
  // change that triggered it. Without a descriptor this fell through to the
  // generic 'Payroll readiness issue' title and surfaced the trigger's raw
  // catch-all sentence verbatim, which read as a real, static, unresolved
  // blocker even when the row was mid-recalculation and nothing was actually
  // outstanding (Medea confirm-then-approve outage, 17 Sep 2026).
  SOURCE_PAY_INPUT_CHANGED: {
    title: 'Pay context is recalculating',
    detail:
      'Inputs changed and payroll must recheck pricing before payment. This pricing warning does not prevent approval of verified worked time; any separate time or evidence issue still needs resolving.',
  },
  ASSIGNMENT_WORK_SEGMENT_CHANGED: {
    title: 'Pay needs recalculating after a roster change',
    detail:
      'The assignment change invalidated its pay calculation. Verified worked time can still be approved. Payroll must recheck the current assignment pricing before payment.',
  },
  [C.MISSING_COMPLETED_TIME_SOURCE_EVIDENCE]: {
    title: 'Completed-time evidence is missing',
    detail:
      'Add how the finished times were captured (method plus supervisor, source reference, or sign-off).',
  },
  [C.AUTO_CLOSED_PENDING_CONFIRMATION]: {
    title: 'Auto-closed after a missed clock-out',
    detail:
      'The end time was set to the scheduled end by the attendance sweep. Confirm or correct the actual times before approval.',
  },
  [C.IMPLAUSIBLE_WORKED_DURATION]: {
    title: 'Worked duration is implausibly short',
    detail:
      'The clocked span is under five minutes — usually an accidental tap. Correct the actual times (or void the row if no work happened) before approval.',
  },
  [C.POSSIBLE_DUPLICATE_TIMESHEET]: {
    title: 'Possible duplicate timesheet',
    detail:
      'Another timesheet overlaps this worked window for the same worker. Resolve the pair so hours are not paid twice.',
  },
  [C.CLOCK_HONESTY]: {
    title: 'Clock-honesty evidence failed',
    detail:
      'The captured clock failed honesty checks (device clock, GPS, or paper-path integrity). Correct the actuals before approval.',
  },
  [C.SERVICE_AGREEMENT_GAP]: {
    title: 'Service agreement missing or stale',
    detail:
      'This billed visit has no current signed service agreement. Resolve the agreement before approval.',
  },
  [C.CARE_PLAN_GAP]: {
    title: 'Care-plan acknowledgement missing',
    detail:
      'This billed visit has no acknowledged care-plan inclusion. Resolve the care plan before approval.',
  },
  [C.AWARD_CLASSIFICATION_MISSING]: {
    title: 'This worker has no pay classification set',
    detail:
      'Set the worker’s award classification on their pay profile before payroll can calculate.',
  },
  [C.UNSUPPORTED_AWARD]: {
    title: 'Award is not supported for automatic payroll',
    detail:
      'Automatic export supports SCHADS, Nurses, Aged Care, and HPSS awards. Review this timesheet under the recorded award.',
  },
  [C.AWARD_REVIEW_REQUIRED]: {
    title: 'Award classification needs payroll review',
    detail: 'A payroll officer must review the recorded award classification before export.',
  },
  [C.ZERO_PAY]: {
    title: 'Pay calculation is missing or zero',
    detail:
      'Hours were worked but calculated pay is $0. Recompute or fix classification and rates.',
  },
  TIMESHEET_ZERO_PAY: {
    title: 'Pay calculation is missing or zero',
    detail: 'This timesheet has worked hours but $0 pay. Recompute after fixing pay setup.',
  },
  [C.OPEN_PAYROLL_DISPUTE]: {
    title: 'Open payroll dispute',
    detail: 'Resolve outstanding payroll disputes on this timesheet before approval or export.',
  },
  [C.FT_OVER_8H_WITHOUT_10H_AGREEMENT]: {
    title: 'Full-time shift over 8 hours without a 10-hour agreement',
    detail:
      'Record a 10-hour ordinary-hours agreement for this full-time worker, or adjust the shift length.',
  },
  [C.PAY_CONTEXT_NOT_RESOLVED]: {
    title: 'Pay role / export context is not resolved',
    detail: 'Resolve the worker’s pay role and export context so payroll can price this shift.',
  },
  [C.BREAK_DURATION_UNRESOLVED]: {
    title: 'Break length is unknown',
    detail:
      'Use Correct actual time to enter the break minutes or confirm that no meal break was taken. Approval waits for this correction; clock-in and clock-out remain available.',
  },
  [C.BREAK_TREATMENT_BLOCKED]: {
    title: 'Break treatment is blocked',
    detail:
      'Record the required break-treatment facts on this timesheet, then have a different payroll reviewer approve them before the pay run snapshot.',
  },
  [C.BREAK_TREATMENT_NOT_RESOLVED]: {
    title: 'Break treatment is not resolved',
    detail:
      'Record the meal/rest-break evidence on this timesheet, then have a different payroll reviewer approve it before payroll export.',
  },
  [C.BREAK_STATUS_UNKNOWN]: {
    title: 'Break treatment status is unknown',
    detail:
      'Payroll must resolve how recorded meal/rest breaks are priced before payment. Verified worked time can still be approved.',
  },
  [C.BREAK_TREATMENT_FACTS_MISSING]: {
    title: 'Break treatment facts are missing',
    detail:
      'Use Record break treatment facts on this timesheet; a different payroll reviewer must approve the submitted evidence.',
  },
  [C.BREAK_NESTED]: {
    title: 'Break treatment issue',
    detail:
      'Record the required break-treatment facts on this timesheet and send them for independent payroll review.',
  },
  [C.PAY_EVENT_FACTS_NOT_READY]: {
    title: 'Manual pay facts need approval',
    detail: 'Capture and approve required manual pay facts for this event before payroll export.',
  },
  [C.PROVIDER_MANUAL_ALLOWANCE_PRICING_PENDING]: {
    title: 'Provider allowance pricing needs attention',
    detail:
      'The allowance evidence is saved. Resolve its pay context and price, then refresh payroll certification before pay-run inclusion.',
  },
  [C.PAY_CONTEXT_NESTED]: {
    title: 'Pay context issue',
    detail: 'Resolve the pay-context blocker on this timesheet or assignment.',
  },
  [C.PAY_FACT_NESTED]: {
    title: 'Pay fact issue',
    detail: 'Resolve the missing or unapproved pay fact for this event.',
  },
  // Period aggregator
  ASSIGNMENT_PAY_CONTEXT_BLOCKED: {
    title: 'Assignment pay context is blocked',
    detail: 'Fix the assignment’s pay role or classification so the shift can be priced.',
  },
  PAY_IDENTITY_CORRECTION_PENDING: {
    title: 'Pay identity correction awaits review',
    detail:
      'Have a separate authorised payroll reviewer decide the proposed correction before release.',
  },
  PAY_IDENTITY_CORRECTION_READ_UNAVAILABLE: {
    title: 'Correction records could not be checked',
    detail: 'Refresh readiness and verify correction records before payroll release.',
  },
  PAY_IDENTITY_CORRECTION_PENDING_APPROVAL: {
    title: 'Pay identity correction awaits approval',
    detail: 'A separate payroll reviewer must decide the proposed correction.',
  },
  PAY_IDENTITY_CORRECTION_PENDING_RUN: {
    title: 'Approved backpay awaits an open run',
    detail: 'Assign the approved correction to the next eligible payroll run.',
  },
  PAY_IDENTITY_CORRECTION_PENDING_SETTLEMENT: {
    title: 'Backpay awaits verified settlement',
    detail: 'Complete the linked run and record verified payment evidence.',
  },
  PAY_IDENTITY_OVERPAYMENT_DECISION_PENDING: {
    title: 'Overpayment needs a provider decision',
    detail: 'Review the negative difference; no deduction is automatic.',
  },
  PAYMENT_ATTESTED_BY_TWO_PEOPLE: {
    title: 'Payment is attested',
    detail: 'Two authorised officers attested payment under the approved tenant policy.',
  },
  LEGACY_RECEIPT_TRANSITIONAL: {
    title: 'Legacy receipt (transitional rule)',
    detail:
      'This approved receipt is accepted for the current pay period under the temporary platform policy. Later periods need a new pay approval.',
  },
  APPROVED_PENDING_RUN: {
    title: 'Approved correction awaits a run',
    detail: 'The approved correction will be linked to the next eligible payroll run.',
  },
  OVERPAYMENT_REVIEW: {
    title: 'Overpayment review pending',
    detail: 'The provider must decide how to handle the negative difference.',
  },
  ASSIGNMENT_SETUP_CHANGED_AFTER_APPROVAL: {
    title: 'Shift setup changed after approval',
    detail: 'The approved timesheet governs this shift. Review the later setup change separately.',
  },
  ASSIGNMENT_PAY_CONTEXT_NOT_RESOLVED: {
    title: 'Assignment pay context is not resolved',
    detail: 'Complete pay-role / pay-profile setup for this roster assignment.',
  },
  ASSIGNMENT_NO_TIMESHEET: {
    title: 'Assignment has no timesheet',
    detail: 'Create or link a timesheet for this completed roster assignment.',
  },
  ASSIGNMENT_COVERED_BY_APPROVED_LEAVE: {
    title: 'Roster shift is covered by approved leave',
    detail: 'Leave is paid instead of a timesheet. Release the unused roster shift to tidy it up.',
  },
  ASSIGNMENT_OFFER_NOT_RESOLVED: {
    title: 'Roster offer is still outstanding',
    detail:
      'Have the worker accept the offer in the worker app, or complete the manager replacement flow before payroll.',
  },
  PROVIDER_MANUAL_ALLOWANCE_RECONCILIATION_REQUIRED: {
    title: 'A manual allowance on this timesheet does not hold up',
    detail:
      'A provider-entered allowance fails its governed contract (eligibility, units, rate, amount, evidence, or the same component recorded twice). Replace it with one effective component through approved-timesheet correction before payroll.',
  },
  MANUAL_PAY_FACT_PROJECTION_PENDING: {
    title: 'Manual allowance facts await pay setup',
    detail:
      'The worked time and allowance facts are safely recorded. Resolve the worker pay profile, then replay/recompute this timesheet before payroll.',
  },
  ALLOWANCE_RECOMPUTE_REQUIRED: {
    title: 'Allowance pricing needs recompute',
    detail:
      'The allowance facts are recorded. Resolve pay setup and recompute before adding the row to payroll.',
  },
  ALLOWANCE_PAY_CERTIFICATION_REQUIRED: {
    title: 'Approved timesheet needs its pay re-certified',
    detail:
      'Hours stay approved. A payroll officer, finance officer or facility manager must refresh certification before this timesheet can enter a pay run.',
  },
  TIMESHEET_REJECTED: {
    title: 'Rejected timesheet is left out of this pay run',
    detail:
      'A rejected timesheet is not paid. If the shift was worked, reopen it from the timesheets grid (Reopen action on the rejected row, with reason and evidence). It only holds the pay run when it is already locked to one; reopen it or escalate for an audited non-payable source closeout.',
  },
  TIMESHEET_AND_APPROVED_LEAVE_SAME_DAY: {
    title: 'Timesheet and approved leave overlap',
    detail:
      'Check whether the worker worked part of the day. Reject the timesheet if leave replaced the shift, or confirm the leave hours before paying both.',
  },
  TIMESHEET_ROSTER_PERIOD_LINKAGE_REQUIRED: {
    title: 'Timesheet is missing its roster period',
    detail: 'Run the audited period relink from payroll readiness.',
  },
  TIMESHEET_ROSTER_IDENTITY_MISMATCH: {
    title: 'Timesheet and roster identities do not match',
    detail:
      'The linked worker, shift or slot differs from the assignment and needs controlled source-linkage repair.',
  },
  TIMESHEET_ROSTER_ASSIGNMENT_SCOPE_MISMATCH: {
    title: 'Timesheet assignment is outside this roster period',
    detail:
      'The assignment pointer is missing or belongs to another period and needs controlled source-linkage repair.',
  },
  TIMESHEET_ROSTER_PERIOD_STATUS_REVIEW_REQUIRED: {
    title: 'Approved or paid timesheet is missing its roster period',
    detail:
      'Automatic relink is not allowed for this status and the corrections workbench cannot repair the period link.',
  },
  TIMESHEET_ROSTER_LIFECYCLE_REVIEW_REQUIRED: {
    title: 'Timesheet conflicts with terminal roster history',
    detail:
      'The linked assignment is cancelled, replaced or unassigned and needs controlled lifecycle reconciliation.',
  },
  XERO_COMPONENT_LINES_MISSING: {
    title: 'Pay-run component-line evidence is missing',
    detail:
      'The existing pay run has provider-managed EA items without materialised component lines and needs payroll-engine escalation.',
  },
  TIMESHEET_PAY_CONTEXT_NOT_RESOLVED: {
    title: 'Timesheet pay context is not resolved',
    detail: 'Re-resolve pay context so the timesheet has a valid pay snapshot.',
  },
  TIMESHEET_PAY_CONTEXT_STALE: {
    title: 'Timesheet pay context is stale',
    detail: 'Recompute the timesheet to refresh its pay-context snapshot.',
  },
  RATE_SCHEDULE_GAP: {
    title: 'Rate schedule gap',
    detail: 'No rate line matches this worker’s classification in the active rate schedule.',
  },
  WORKER_MISSING_PAY_PROFILE: {
    title: 'Worker has no pay profile',
    detail: 'Create an active pay profile for this worker covering the pay period.',
  },
  WORKED_TIME_HOLD_UNDECIDED: {
    title: 'Worked-time review needs a decision',
    detail: 'Open the timesheet and choose how to pay the time before paying this worker.',
  },
  WORKED_TIME_PAYABLE_MISSING: {
    title: 'Worked-time pay times are missing',
    detail: 'Re-open the worked-time review to restore the payable times for this timesheet.',
  },
  WORKED_TIME_HOLDS_NEAR_PAY_DATE: {
    title: 'Worked-time reviews are near the pay date',
    detail: 'Undecided worked-time reviews will keep those workers from being paid.',
  },
  WORKED_TIME_HOLDS_READ_UNAVAILABLE: {
    title: 'Worked-time holds could not be checked',
    detail: 'Refresh readiness before payroll release so worked-time holds can be checked.',
  },
  EBA_EXPORT_NOT_READY: {
    title: 'Enterprise agreement export is not ready',
    detail:
      'Finish EA export setup (mappings / dry-run) before sending this payroll to the provider.',
  },
  PAY_PROFILE_MISSING_PAY_RULE_VERSION: {
    title: 'Pay profile has no pay-rule version',
    detail:
      'The worker’s active pay profile is not bound to a certified pay-rule version, so nothing can be priced. Open the pay profile and select the agreement version.',
  },
  PAY_RATE_UNSUPPORTED_BY_AGREEMENT: {
    title: 'Pay rate is not in the agreement',
    detail:
      'The rate on the pay profile does not match any rate the agreement supports for this classification. Advisory: check the profile before export.',
  },
  CLASSIFICATION_DIFFERS_FROM_RECORD: {
    title: 'Classification differs from the worker record',
    detail:
      'The classification used for pricing differs from the one on the worker’s record. Advisory: confirm which is right before export.',
  },
  PAY_ROLE_CARE_MINUTES_CATEGORY_MISSING: {
    title: 'Pay role missing care-minutes category',
    detail:
      'Payroll setup follow-up: review the pay role care-minutes category. This advisory does not block approval, snapshot or export.',
  },
  XERO_TEMPLATE_DISAGREES: {
    title: 'Xero pay template disagrees with the intended payslip',
    detail:
      'Statura compared the lines it intends to send with the worker’s own Xero pay template and found a different item, rate, category or unit type. Advisory: review before writing payslips.',
  },
  UNPAID_PENDING_LEAVE: {
    title: 'Absence has no leave request',
    detail:
      'An absence past its due date has no approved leave request, so it is not paid in this run. Leave is paid only when the worker requests it; if they do, create the leave on their behalf. This does not block the pay run.',
  },
  // Nested pay-context (provider vocabulary; seed from PAY_CONTEXT_BLOCKER_LABELS where present)
  PAY_ROLE_REQUIRED: {
    title: 'More than one pay role could fit this shift',
    detail: PAY_ROLE_REQUIRED_MESSAGE,
  },
  PAY_ROLE_RESELECTION_REQUIRED: {
    title: 'Pay role needs to be reselected',
    detail: 'Select the worker’s current pay role for this timesheet, then re-check the shift.',
  },
  PAY_ROLE_ASSIGNMENT_REQUIRED: {
    title: 'Pay role is not assigned to this worker',
    detail: 'Assign the selected pay role to the worker for the shift date.',
  },
  PAY_ROLE_ASSIGNMENT_STALE: {
    title: 'Roster pins a pay role that is no longer assigned',
    detail:
      'The roster assignment still carries a pay-role pin that ended or was replaced. Re-pin the assignment to the worker’s currently active pay role.',
  },
  PAY_ROLE_NOT_FOUND: {
    title: 'Pay role was not found',
    detail: 'The selected pay role no longer exists for this organisation.',
  },
  PAY_ROLE_NOT_ACTIVE: {
    title: 'Pay role is not active',
    detail: 'Activate the pay role or choose an active role for this shift.',
  },
  PAY_ROLE_ELIGIBILITY_BLOCKED: {
    title: 'Worker is not eligible for this pay role',
    detail: 'Update the worker’s pay-role assignment eligibility or choose a different role.',
  },
  PAY_ROLE_AMBIGUOUS: {
    title: 'Multiple pay roles match',
    detail: 'Choose one pay role explicitly — more than one role could apply.',
  },
  PAY_ROLE_MISSING: {
    title: 'Pay role is missing',
    detail: 'Select a pay role for this worker on the assignment.',
  },
  STAFF_REQUIRED: {
    title: 'Staff member is required',
    detail: 'Pay context can only resolve when the shift is assigned to a staff member.',
  },
  STAFF_PAY_PROFILE_NOT_FOUND: {
    title: 'No pay profile covers this worker',
    detail: 'Create or extend a pay profile for this worker covering the shift date.',
  },
  STAFF_PAY_PROFILE_AMBIGUOUS: {
    title: 'Multiple pay profiles cover this worker',
    detail:
      'More than one active pay profile overlaps the shift date. Close or supersede the extra open-ended rows so exactly one profile is effective.',
  },
  STAFF_CLASSIFICATION_NOT_FOUND: {
    title: 'This worker has no pay classification set',
    detail: 'Add classification history for this worker covering the shift date.',
  },
  RATE_NOT_FOUND: {
    title: 'No matching rate line',
    detail: 'Add a rate line for this worker’s classification in the active rate schedule.',
  },
  RATE_CONTEXT_NOT_FOUND: {
    title: 'Rate context could not be loaded',
    detail: 'Check the active award rate version and EBA configuration for this organisation.',
  },
  PAY_FACTS_MISSING: {
    title: 'Required pay facts are missing',
    detail: 'Capture the missing manual pay facts for this event.',
  },
  PAY_FACT_SET_REQUIRED: {
    title: 'Pay facts must be captured',
    detail: 'Open the pay-facts form and supply the required facts for this event.',
  },
  PAY_FACT_SET_NOT_APPROVED: {
    title: 'Allowance answers on this timesheet still need a second approver',
    detail: 'Approve the current timesheet pay facts before adding this shift to payroll.',
  },
  PAY_RULE_TWO_ORACLE_CERTIFICATION_REQUIRED: {
    title: 'Enterprise agreement certification is required',
    detail:
      'Complete a passed two-oracle certification for the current pay-rule configuration before using it for payroll.',
  },
  PAY_CONTEXT_ORDINARY_PROFILE_DRIFT: {
    title: 'Ordinary-hours profile does not match',
    detail:
      'The configured ordinary-hours profile differs from the worker’s active profile — review and re-resolve.',
  },
  PAY_CONTEXT_RATE_IDENTITY_DRIFT: {
    title: 'Award rate version has drifted',
    detail:
      'The configured award rate version differs from the resolved rate. Review configuration; this is advisory unless export enforcement is on.',
  },
  PAY_CONTEXT_EBA_IDENTITY_DRIFT: {
    title: 'Enterprise agreement has drifted',
    detail:
      'The configured EBA differs from the resolved agreement. Review configuration; this is advisory unless export enforcement is on.',
  },
  PAY_CONTEXT_RULE_IDENTITY_DRIFT: {
    title: 'Pay rule version has drifted',
    detail:
      'The configured pay rule version differs from the resolved rule. Review configuration; this is advisory unless export enforcement is on.',
  },
  SALARY_FLAT_REVIEW_REQUIRED: {
    title: 'Salary-flat reconciliation review required',
    detail: 'Complete annualised-salary reconciliation review before pay context can resolve.',
  },
  SALARY_FLAT_CONFIG_INCOMPLETE: {
    title: 'Salary-flat configuration is incomplete',
    detail: 'Set annualised salary and pay cadence for this salary-flat worker.',
  },
  CONTRACT_HOURLY_CONFIG_MISSING: {
    title: 'Contract-hourly configuration is missing',
    detail: 'Create effective-dated contract-hourly terms in the fixed-pay workbench.',
  },
  CONTRACT_HOURLY_RATE_REQUIRED: {
    title: 'Contract-hourly base rate is missing',
    detail: "Set the worker's contract hourly rate in the fixed-pay workbench.",
  },
  CONTRACT_WEEKLY_HOURS_REQUIRED: {
    title: 'Contract weekly hours are missing',
    detail: "Set the worker's contracted weekly hours in the fixed-pay workbench.",
  },
  CONTRACT_OVERTIME_TERMS_REQUIRED: {
    title: 'Contract overtime terms are missing',
    detail: "Record the worker's overtime terms in the fixed-pay workbench.",
  },
  CONTRACT_PENALTY_TERMS_REQUIRED: {
    title: 'Contract penalty terms are missing',
    detail: "Record the worker's penalty terms in the fixed-pay workbench.",
  },
  CONTRACT_TERMS_EVIDENCE_REQUIRED: {
    title: 'Contract terms evidence is missing',
    detail: "Attach evidence supporting the worker's contract-hourly terms.",
  },
  CONTRACT_CONFIGURATION_NOT_APPROVED: {
    title: 'Contract-hourly configuration needs approval',
    detail: 'An independent checker must approve the contract-hourly configuration.',
  },
  CONTRACT_HOURLY_MATERIALISER_NOT_CONNECTED: {
    title: 'Contract-hourly worked-pay calculation is not connected',
    detail:
      'Worked-pay materialisation is not connected; no operational action can clear this blocker.',
  },
  CONTRACT_HOURLY_TREATMENT_NOT_APPROVED: {
    title: 'Contract-hourly treatment needs approval',
    detail: 'Create and independently approve structured contract-hourly treatment rules.',
  },
  CONTRACT_HOURLY_TREATMENT_INVALID: {
    title: 'Contract-hourly treatment is invalid or stale',
    detail: 'Replace the treatment against the current approved contract configuration.',
  },
  // Export family
  XERO_MAPPING_MISSING: {
    title: 'Xero earnings mappings are missing',
    detail: 'Map Statura pay components to Xero earnings rates before export.',
  },
  XERO_MAPPING_DRIFT: {
    title: 'Xero earnings mappings have drifted',
    detail: 'Refresh Xero mappings so exported components still match the live catalog.',
  },
  DRY_RUN_NOT_RUN: {
    title: 'Payroll dry-run has not been run',
    detail: 'Run a dry-run reconciliation before the first live export for this agreement.',
  },
  DRY_RUN_FAILED: {
    title: 'Payroll dry-run failed',
    detail: 'Resolve dry-run failures before allowing a live payroll export.',
  },
  DRY_RUN_RECONCILIATION: {
    title: 'Dry-run reconciliation required',
    detail: 'Complete dry-run reconciliation evidence before live export.',
  },
  // Representative provider-responsibility evidence code (open-ended family uses prefix)
  [providerResponsibilityEvidenceCode('TAX')]: {
    title: 'Provider responsibility evidence is missing: TAX',
    detail: 'Add evidence for provider responsibility TAX before payroll export.',
  },
  // Controlled-source readiness (period loader calculation blockers)
  TIMESHEET_CORRECTION_PENDING_REVIEW: {
    title: 'Timesheet correction is waiting for review',
    detail:
      'An approved timesheet has a pending correction that needs an independent checker decision before payroll can proceed.',
  },
  IMPORTED_LEAVE_PENDING_APPROVAL: {
    title: 'Imported leave is waiting for approval',
    detail:
      'Provider-imported leave is still pending independent coverage-aware approval before the pay period can close.',
  },
  AVERAGING_CYCLE_DIFFERS_FROM_ROSTER: {
    title: 'Overtime averaging cycle differs from the published roster',
    detail:
      'The overtime averaging cycle recorded in Payroll > Policy does not match this roster period (its length or start day differs). Overtime is counted on the recorded cycle, which must be the roster cycle you publish. Change the cycle or the roster so they match.',
  },
  PUBLIC_HOLIDAY_LEAVE_CREDIT_NOT_POSTABLE: {
    title: 'Public holiday leave choice cannot be honoured',
    detail:
      'A worker chose to take public holiday hours as annual leave, but the leave cannot be added in Xero (leave sending is off, Xero is not connected, Annual Leave is not mapped, or the worker is not matched to a Xero employee), or the worker’s employment type has no choice. Until it is fixed, their public holiday hours are paid in cash at the extra 150% instead. Nothing is held.',
  },
  PAY_CLOCK_BASE_CALENDAR_UNAVAILABLE: {
    title: 'Worker base location could not be read',
    detail:
      "This provider works in more than one timezone or state, and the worker's base location could not be read, so their base public holidays cannot be checked. This shift is held from the pay run until the check succeeds. Refresh readiness to retry. Other workers' pay and care delivery are not affected.",
  },
  LEAVE_ROSTER_EVIDENCE_GAP: {
    title: 'Shiftworker leave roster evidence is incomplete',
    detail:
      'Roster and release every shift the worker would have worked during the annual-leave period, or defer the request before payroll snapshot.',
  },
  LEAVE_PAY_CONTEXT_BOUNDARY: {
    title: 'Leave pay context changes inside the leave period',
    detail:
      "The worker's classification, pay profile or rate version changes part-way through this approved leave, so one rate cannot price it. Open the leave request, confirm the effective dates, and split or defer the request before payroll snapshot.",
  },
  LEAVE_PAY_CONTEXT_MISSING: {
    title: 'Leave pay context is missing',
    detail:
      'No classification history, pay profile or effective rate version covers this approved leave. Open the leave request to see which is missing, fix it on the worker or the rate schedule, then re-run readiness.',
  },
  PUBLIC_HOLIDAY_ABSENCE_STALE_PAYMENT: {
    title: 'Statura must reconcile saved public holiday pay',
    detail:
      'The saved holiday pay no longer matches current leave entitlement. Ask Statura support to reconcile it. Pay is held; care continues.',
  },
  PUBLIC_HOLIDAY_ABSENCE_AWARD_SUPPORT_REQUIRED: {
    title: 'Statura must add public holiday pay',
    detail:
      'This award needs Statura support to calculate public holiday paid absence. Pay is held; care continues.',
  },
  PUBLIC_HOLIDAY_XERO_MAPPING_REQUIRED: {
    title: 'Map your Public Holiday pay item',
    detail:
      'Choose an ordinary-time earnings rate in Xero earnings mappings. Pay is held until it is mapped; care continues.',
  },
  APPROVED_LEAVE_PUBLIC_HOLIDAY_PAYMENT_EVIDENCE_MISSING: {
    title: 'Public-holiday leave payment evidence is missing',
    detail:
      'Approved leave overlaps a public holiday. Prove the separate public-holiday-not-worked pay event before payroll snapshot.',
  },
  APPROVED_LEAVE_PAY_TREATMENT_UNSUPPORTED: {
    title: 'Approved leave has no automatic pay treatment',
    detail:
      'This leave type has no configured pay-run treatment. Correct the type or complete a controlled provider handoff before snapshot.',
  },
  LEAVE_WORKER_SCOPE_ADJUDICATED: {
    title: 'Payroll chose the facility for this leave',
    detail:
      'The roster did not show one clear facility for this leave, so payroll picked one and the leave will be paid on that basis. Check the choice is right, or undo it if it is not. Nothing is held.',
  },
};

function seedFromPayContextLabels(): void {
  for (const [code, label] of Object.entries(PAY_CONTEXT_BLOCKER_LABELS)) {
    if (DESCRIPTOR_OVERRIDES[code]) continue;
    DESCRIPTOR_OVERRIDES[code] = {
      title: label,
      detail: label,
    };
  }
}

seedFromPayContextLabels();

/** Full registry: code → descriptor (includes fixScope). */
export const BLOCKER_DESCRIPTOR_REGISTRY: Readonly<Record<string, BlockerDescriptor>> =
  Object.freeze(
    Object.fromEntries(
      Object.entries(DESCRIPTOR_OVERRIDES).map(([code, seed]) => [
        code,
        {
          code,
          title: seed.title,
          detail: seed.detail,
          fixScope: fixScopeForCode(code),
          remedyCapability: remedyCapabilityForCode(code),
        },
      ]),
    ),
  );

function descriptorForProviderResponsibility(code: string): BlockerDescriptor | null {
  const kind = providerResponsibilityKindFromCode(code);
  if (!kind) return null;
  return {
    code,
    title: `Provider responsibility evidence is missing: ${kind}`,
    detail: `Add evidence for provider responsibility ${kind} before payroll export.`,
    fixScope: fixScopeForCode(code),
    remedyCapability: remedyCapabilityForCode(code),
  };
}

function descriptorForApprovedLeave(code: string): BlockerDescriptor | null {
  if (!isApprovedLeaveReadinessCode(code)) return null;
  if (code.endsWith('_REQUIRES_CONTROLLED_HANDOFF')) {
    return {
      code,
      title: 'Approved leave requires controlled payroll handoff',
      detail:
        'This leave type needs a controlled provider handoff with payment evidence before payroll snapshot.',
      fixScope: fixScopeForCode(code),
      remedyCapability: remedyCapabilityForCode(code),
    };
  }
  return {
    code,
    title: 'Approved leave pay treatment issue',
    detail: 'Resolve the approved-leave pay treatment before including it in the payroll run.',
    fixScope: fixScopeForCode(code),
    remedyCapability: remedyCapabilityForCode(code),
  };
}

export function getBlockerDescriptor(code: string): BlockerDescriptor | null {
  const exact = BLOCKER_DESCRIPTOR_REGISTRY[code];
  if (exact) return exact;
  if (isProviderResponsibilityEvidenceCode(code)) {
    return descriptorForProviderResponsibility(code);
  }
  if (isApprovedLeaveReadinessCode(code)) {
    return descriptorForApprovedLeave(code);
  }
  return null;
}

/**
 * Last-resort presentation for a code with no descriptor. Not a substitute for
 * writing one — it exists so an authoring gap degrades to something a payroll
 * officer can read rather than to a raw enum token.
 */
function humaniseBlockerCode(code: string): string {
  const words = code
    .trim()
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) => word.toLowerCase());
  if (words.length === 0) return 'A payroll readiness check';
  return (
    words[0]!.charAt(0).toUpperCase() +
    words[0]!.slice(1) +
    (words.length > 1 ? ' ' + words.slice(1).join(' ') : '')
  );
}

/**
 * Descriptor for a code, with a safe fallback for unknown codes (should not fire
 * for enumeration-covered codes once the coverage test is green).
 */
export function describeBlocker(code: string): BlockerDescriptor {
  const known = getBlockerDescriptor(code);
  if (known) return known;
  return {
    code,
    title: 'Payroll readiness issue',
    // The raw code must never reach the screen. A blocker with no descriptor is
    // an authoring gap, not something to show a payroll officer:
    // `WORKER_PAY_IDENTITY_CHANGED` was rendered verbatim on the blocked queue
    // until 9 Sep 2026. `code` is still carried on the descriptor for logs and
    // support, which is where it belongs.
    detail: `${humaniseBlockerCode(code)} needs attention before payroll can continue.`,
    fixScope: fixScopeForCode(code),
    remedyCapability: remedyCapabilityForCode(code),
  };
}

/**
 * Static seed of known descriptor keys (nested labels + registries).
 *
 * CI descriptor coverage must derive from `discoverEmittedReadinessCodesFromSources()`
 * so a newly-emitted loader code without a descriptor fails. This helper remains
 * for fixtures / docs; it is intentionally not the CI gate.
 */
export function codesRequiringDescriptors(): string[] {
  const codes = new Set(allEmittedReadinessCodes());
  for (const code of PERIOD_AGGREGATOR_BLOCKER_CODES) codes.add(code);
  for (const code of KNOWN_PAY_CONTEXT_NESTED_CODES) codes.add(code);
  for (const code of KNOWN_EXPORT_BLOCKER_CODES) codes.add(code);
  for (const code of Object.keys(PAY_CONTEXT_BLOCKER_LABELS)) codes.add(code);
  // Controlled-source readiness (period loader)
  codes.add('TIMESHEET_CORRECTION_PENDING_REVIEW');
  codes.add('IMPORTED_LEAVE_PENDING_APPROVAL');
  codes.add('LEAVE_ROSTER_EVIDENCE_GAP');
  codes.add('PAY_CLOCK_BASE_CALENDAR_UNAVAILABLE');
  codes.add('AVERAGING_CYCLE_DIFFERS_FROM_ROSTER');
  codes.add('PUBLIC_HOLIDAY_LEAVE_CREDIT_NOT_POSTABLE');
  codes.add('LEAVE_PAY_CONTEXT_BOUNDARY');
  codes.add('LEAVE_PAY_CONTEXT_MISSING');
  codes.add('APPROVED_LEAVE_PUBLIC_HOLIDAY_PAYMENT_EVIDENCE_MISSING');
  codes.add('PUBLIC_HOLIDAY_ABSENCE_AWARD_SUPPORT_REQUIRED');
  codes.add('PUBLIC_HOLIDAY_ABSENCE_STALE_PAYMENT');
  codes.add('PUBLIC_HOLIDAY_XERO_MAPPING_REQUIRED');
  codes.add('APPROVED_LEAVE_PAY_TREATMENT_UNSUPPORTED');
  codes.add('LEAVE_WORKER_SCOPE_ADJUDICATED');
  codes.add('TIMESHEET_FACT_SET_APPROVAL_PENDING');
  // Representative open-ended family members (prefix descriptors cover all variants)
  codes.add(providerResponsibilityEvidenceCode('TAX'));
  codes.add('APPROVED_LEAVE_SAMPLE_REQUIRES_CONTROLLED_HANDOFF');
  return Array.from(codes).sort();
}
